import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, collection, query, where, onSnapshot } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import {
  MessageCircle, Search, ChevronRight, User
} from 'lucide-react';

const ProfessionalInboxPage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  useEffect(() => {
    if (!user?.uid) return;

    // ✅ Properly scoped query — only this pro's messages
    const q = query(
      collection(db, 'messages'),
      where('professionalId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setMessages(data);
        setLoading(false);
      },
      (error) => {
        console.error('Error streaming messages:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Group messages by clientId → build conversation summaries
  const conversations = useMemo(() => {
    const map = new Map();

    messages.forEach((msg) => {
      const clientId = msg.clientId || msg.senderId || msg.from || 'unknown';
      if (!map.has(clientId)) {
        map.set(clientId, {
          clientId,
          clientName: msg.clientName || 'Client',
          clientImage: msg.clientImage || null,
          messages: [],
        });
      }
      map.get(clientId).messages.push(msg);
    });

    return Array.from(map.values()).map((conv) => {
      // Sort messages newest first
      conv.messages.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });

      const latest = conv.messages[0];
      const unreadCount = conv.messages.filter(
        (m) => m.sender === 'client' && m.read === false
      ).length;

      // Pick a display name — prefer the latest msg's clientName if available
      const displayName =
        latest?.clientName || conv.clientName || 'Client';
      const displayImage =
        latest?.clientImage || conv.clientImage || null;

      return {
        clientId: conv.clientId,
        clientName: displayName,
        clientImage: displayImage,
        lastMessage: latest?.text || latest?.message || '',
        lastTimestamp: latest?.createdAt,
        lastSender: latest?.sender,
        unreadCount,
        messageCount: conv.messages.length,
      };
    });
  }, [messages]);

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (filter === 'unread') list = list.filter((c) => c.unreadCount > 0);
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          c.clientName.toLowerCase().includes(t) ||
          c.lastMessage.toLowerCase().includes(t)
      );
    }
    // Sort by most recent
    return list.sort((a, b) => {
      const aT = a.lastTimestamp?.toMillis?.() ?? new Date(a.lastTimestamp || 0).getTime();
      const bT = b.lastTimestamp?.toMillis?.() ?? new Date(b.lastTimestamp || 0).getTime();
      return bT - aT;
    });
  }, [conversations, filter, searchTerm]);

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="bg-[#F4EEFF] rounded-3xl p-6 md:p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">Messages</span>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">Inbox</h1>
            <p className="text-gray-500 mt-1.5 text-sm">
              {conversations.length > 0
                ? `${conversations.length} ${conversations.length === 1 ? 'conversation' : 'conversations'}${totalUnread > 0 ? ` · ${totalUnread} unread` : ''}`
                : 'Chat with clients who reach out'}
            </p>
          </div>
        </div>

        {conversations.length > 0 && (
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Conversations</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{conversations.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Unread</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{totalUnread}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Messages</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{messages.length}</p>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* FILTER BAR */}
      {/* ============================================ */}
      {conversations.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-white border border-gray-100 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
            />
          </div>
          <div className="flex gap-1.5 bg-white border border-gray-100 rounded-full p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                filter === 'all'
                  ? 'bg-[#010101] text-white'
                  : 'text-gray-500 hover:bg-[#FCF8F9]'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors relative ${
                filter === 'unread'
                  ? 'bg-[#010101] text-white'
                  : 'text-gray-500 hover:bg-[#FCF8F9]'
              }`}
            >
              Unread
              {totalUnread > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] ${
                  filter === 'unread' ? 'bg-white text-[#010101]' : 'bg-[#AF8BD4] text-white'
                }`}>
                  {totalUnread}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CONVERSATIONS LIST */}
      {/* ============================================ */}
      {conversations.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
          <div className="w-24 h-24 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-5">
            <MessageCircle size={36} className="text-[#AF8BD4]" />
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Inbox is empty</h3>
          <p className="text-gray-400 text-sm max-w-sm mx-auto">
            When clients message you, their conversations will appear here.
          </p>
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl border border-gray-50">
          <p className="text-gray-400 text-sm">
            {filter === 'unread' ? 'No unread conversations' : 'No conversations match your search'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((conv) => {
            const isUnread = conv.unreadCount > 0;
            const preview =
              conv.lastSender === 'professional' ? `You: ${conv.lastMessage}` : conv.lastMessage;

            return (
              <button
                key={conv.clientId}
                onClick={() => navigate(`/professional-dashboard/inbox/${conv.clientId}`)}
                className={`w-full text-left bg-white p-4 rounded-3xl border transition-colors flex items-center gap-4 group ${
                  isUnread
                    ? 'border-[#CDBCE7] hover:border-[#AF8BD4]'
                    : 'border-gray-100 hover:border-[#CDBCE7]'
                }`}
              >
                {/* Avatar */}
                <div className="relative shrink-0">
                  <div className="w-14 h-14 rounded-full bg-[#F4EEFF] flex items-center justify-center overflow-hidden">
                    {conv.clientImage ? (
                      <img src={conv.clientImage} alt={conv.clientName} className="w-full h-full object-cover" />
                    ) : (
                      <User size={22} className="text-[#AF8BD4]" />
                    )}
                  </div>
                  {isUnread && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-[#AF8BD4] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 mb-0.5">
                    <h3 className={`truncate text-[15px] ${isUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                      {conv.clientName}
                    </h3>
                    <span className={`text-[11px] shrink-0 ${isUnread ? 'text-[#AF8BD4] font-bold' : 'text-gray-400'}`}>
                      {formatTimestamp(conv.lastTimestamp)}
                    </span>
                  </div>
                  <p className={`text-[13px] truncate ${isUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                    {preview || 'No messages yet'}
                  </p>
                </div>

                {/* Chevron */}
                <ChevronRight size={18} className="text-gray-300 group-hover:text-[#AF8BD4] transition-colors shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* ============================================ */}
      {/* HINT */}
      {/* ============================================ */}
      {conversations.length > 0 && (
        <p className="text-center text-[11px] text-gray-400 mt-6">
          Tap a conversation to open the chat and reply
        </p>
      )}
    </div>
  );
};

export default ProfessionalInboxPage;